import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import {
  normalizePhone,
  isValidIndianMobile,
  getIndianMobileError,
} from '../../utils/phoneValidation';
import {
  sendLoginOtp,
  mapSendOtpError,
  getStoredRateLimitUntil,
  formatRateLimitCountdown,
} from '../../services/loginPhoneApi';

const LoginPhone = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);
  const [rateLimitUntil, setRateLimitUntil] = useState(() => getStoredRateLimitUntil());

  const validationError = touched ? getIndianMobileError(phone) : '';
  const displayError = error || validationError;
  const isRateLimited = rateLimitUntil && rateLimitUntil > Date.now();
  const canSubmit = isValidIndianMobile(phone) && !isLoading && !isRateLimited;

  useEffect(() => {
    if (!rateLimitUntil || rateLimitUntil <= Date.now()) return undefined;
    const id = setInterval(() => {
      const stored = getStoredRateLimitUntil();
      setRateLimitUntil(stored);
    }, 1000);
    return () => clearInterval(id);
  }, [rateLimitUntil]);

  const handlePhoneChange = (e) => {
    const value = normalizePhone(e.target.value).slice(0, 10);
    setPhone(value);
    setError('');
  };

  const handleBlur = () => {
    setTouched(true);
  };

  const handleSendOtp = useCallback(async (e) => {
    e.preventDefault();
    setTouched(true);

    const digits = normalizePhone(phone);
    const fieldError = getIndianMobileError(digits);
    if (fieldError) {
      setError(fieldError);
      return;
    }

    if (!navigator.onLine) {
      toast.error('No internet connection. Check your network.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await sendLoginOtp(digits);
      navigate('/login-otp', {
        replace: true,
        state: {
          phone: digits,
          otpId: result.otpId,
          from: location.state?.from,
        },
      });
    } catch (err) {
      const mapped = mapSendOtpError(err);
      if (mapped.toast) {
        toast.error(mapped.message);
      } else {
        setError(mapped.message);
      }
      if (mapped.rateLimitUntil) {
        setRateLimitUntil(mapped.rateLimitUntil);
      }
    } finally {
      setIsLoading(false);
    }
  }, [phone, navigate, location.state?.from]);

  return (
    <Page>
      <Card>
        <LogoSection>
          <img loading="lazy" src="/sparepe.jpeg" alt="SparePe" className="logo" />
        </LogoSection>

        <Heading>Login to SparePe</Heading>
        <Subheading>Enter your mobile number to continue</Subheading>

        <Form onSubmit={handleSendOtp}>
          <FieldGroup>
            <PhoneRow $hasError={Boolean(displayError)}>
              <Prefix aria-hidden="true">+91</Prefix>
              <PhoneInput
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="tel-national"
                placeholder="10-digit mobile number"
                value={phone}
                onChange={handlePhoneChange}
                onBlur={handleBlur}
                maxLength={10}
                disabled={isLoading || isRateLimited}
                aria-invalid={Boolean(displayError)}
                aria-describedby={displayError ? 'phone-error' : undefined}
              />
            </PhoneRow>
            {displayError && <InlineError id="phone-error">{displayError}</InlineError>}
            {isRateLimited && (
              <InlineError>
                Try again in {formatRateLimitCountdown(rateLimitUntil)}
              </InlineError>
            )}
          </FieldGroup>

          <SubmitButton type="submit" disabled={!canSubmit} $active={canSubmit}>
            {isLoading ? (
              <>
                <FaSpinner className="spin" aria-hidden="true" />
                Sending…
              </>
            ) : (
              'Send OTP'
            )}
          </SubmitButton>
        </Form>

        <TermsLine>
          By continuing, you agree to our{' '}
          <Link to="/terms-of-use">Terms</Link>
          {' & '}
          <Link to="/privacy-policy">Privacy Policy</Link>
        </TermsLine>
      </Card>
    </Page>
  );
};

const Page = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--sp-bg-deep, #f8fafc);
  color: var(--sp-ink, #0f172a);
  padding: 24px 16px;
  color-scheme: light;

  html.dark &,
  body.app-dark-theme & {
    color-scheme: dark;
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LogoSection = styled.div`
  margin-bottom: 32px;

  .logo {
    height: 72px;
    width: auto;
    object-fit: contain;
  }
`;

const Heading = styled.h1`
  margin: 0 0 8px;
  font-size: 28px;
  font-weight: 700;
  color: var(--sp-ink, #1e293b);
  text-align: center;
`;

const Subheading = styled.p`
  margin: 0 0 32px;
  font-size: 15px;
  color: var(--sp-ink-3, #64748b);
  text-align: center;
`;

const Form = styled.form`
  width: 100%;
`;

const FieldGroup = styled.div`
  margin-bottom: 20px;
`;

const PhoneRow = styled.div`
  display: flex;
  border: 1px solid ${(p) => (p.$hasError ? '#dc2626' : 'var(--sp-border, #e2e8f0)')};
  border-radius: 8px;
  overflow: hidden;
  background: var(--sp-bg-card, #fff);
  transition: border-color 0.2s ease;

  &:focus-within {
    border-color: ${(p) => (p.$hasError ? '#dc2626' : 'var(--sp-trust, #2563eb)')};
  }
`;

const Prefix = styled.span`
  display: flex;
  align-items: center;
  padding: 12px 14px;
  background: var(--sp-bg-surface, #f1f5f9);
  color: var(--sp-ink-2, #64748b) !important;
  font-size: 15px;
  font-weight: 500;
  border-right: 1px solid var(--sp-border, #e2e8f0);
  user-select: none;
  pointer-events: none;
`;

const PhoneInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  padding: 12px 14px;
  font-size: 16px;
  color: var(--sp-ink, #0f172a);
  background: transparent;
  caret-color: var(--sp-ink, #0f172a);

  &::placeholder {
    color: var(--sp-ink-3, #94a3b8);
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 1000px var(--sp-bg-card, #ffffff) inset !important;
    box-shadow: 0 0 0 1000px var(--sp-bg-card, #ffffff) inset !important;
    -webkit-text-fill-color: var(--sp-ink, #0f172a) !important;
    caret-color: var(--sp-ink, #0f172a);
  }

  &:disabled {
    background: var(--sp-bg-surface, #f8fafc);
    cursor: not-allowed;
  }
`;

const InlineError = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  color: #dc2626;
  line-height: 1.4;
`;

const SubmitButton = styled.button`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, opacity 0.2s ease;
  background: ${(p) => (p.$active ? '#2563eb' : '#cbd5e1')};
  color: ${(p) => (p.$active ? '#fff' : '#94a3b8')};

  &:disabled {
    cursor: not-allowed;
    opacity: ${(p) => (p.$active ? 0.85 : 1)};
  }

  &:not(:disabled):hover {
    background: #1d4ed8;
  }

  .spin {
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const TermsLine = styled.p`
  margin: 20px 0 0;
  font-size: 12px;
  color: #64748b;
  text-align: center;
  line-height: 1.5;

  a {
    color: #2563eb;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }
`;

export default LoginPhone;
